import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const GET_CURRENT_USER = gql`
  query GetCurrentUser {
    getCurrentUser {
      id
      email
      firstName
      lastName
      role
    }
  }
`;

const LIST_CASES = gql`
  query ListCases($limit: Int) {
    listCases(limit: $limit) {
      items {
        id
        caseNumber
        status
        caseType
        description
        incidentDate
        createdAt
        updatedAt
        clientId
        client {
          id
          firstName
          lastName
          email
        }
        assignedAttorneyId
        assignedCaseManagerId
      }
    }
  }
`;

interface CurrentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
}

interface CaseItem {
  id: string;
  caseNumber: string;
  status: string;
  caseType: string;
  description?: string;
  incidentDate?: string;
  createdAt: string;
  updatedAt: string;
  clientId: string;
  client?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  assignedAttorneyId?: string;
  assignedCaseManagerId?: string;
}

interface GetCurrentUserData {
  getCurrentUser: CurrentUser;
}

interface ListCasesData {
  listCases: {
    items: CaseItem[];
  };
}

export function CasesWidget() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [sortBy, setSortBy] = useState<'date' | 'updated' | 'status'>('updated');
  
  const { data: userData } = useQuery<GetCurrentUserData>(GET_CURRENT_USER);
  
  const { data, loading, error } = useQuery<ListCasesData>(LIST_CASES, {
    variables: { limit: 100 },
    fetchPolicy: 'network-only',
  });

  const cases = data?.listCases?.items || [];
  const currentUser = userData?.getCurrentUser;

  // Filter cases - show only assigned cases on dashboard
  const filteredCases = cases.filter((caseItem: any) => {
    // Status filter
    if (statusFilter !== 'ALL' && caseItem.status !== statusFilter) {
      return false;
    }
    
    // Show only cases assigned to current user
    if (currentUser) {
      const isAssigned = 
        caseItem.assignedAttorneyId === currentUser.id ||
        caseItem.assignedCaseManagerId === currentUser.id ||
        currentUser.role === 'ADMIN'; // Admins see all
      
      return isAssigned;
    }
    
    return true;
  });

  // Sort cases
  const sortedCases = [...filteredCases].sort((a: any, b: any) => {
    if (sortBy === 'date') {
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    } else if (sortBy === 'updated') {
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    } else if (sortBy === 'status') {
      return a.status.localeCompare(b.status);
    }
    return 0;
  });

  const getStatusColor = (status: string) => {
    const colors: Record<string, 'default' | 'warning' | 'success' | 'info'> = {
      NEW: 'info',
      IN_PROGRESS: 'warning',
      NEGOTIATION: 'warning',
      LITIGATION: 'warning',
      SETTLED: 'success',
      CLOSED: 'default',
    };
    return colors[status] || 'default';
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Cases</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aalaw-red"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Cases</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-red-600 text-sm">Error loading cases</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="inquiry-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>My Assigned Cases ({filteredCases.length})</CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/cases')}
          >
            View All →
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex gap-3 mb-4 flex-wrap">
          <div className="flex gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
            >
              <option value="ALL">All Status</option>
              <option value="NEW">New</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="NEGOTIATION">Negotiation</option>
              <option value="LITIGATION">Litigation</option>
              <option value="SETTLED">Settled</option>
              <option value="CLOSED">Closed</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="text-xs px-3 py-1.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-aalaw-red focus:border-transparent"
            >
              <option value="updated">Last Updated</option>
              <option value="date">Date Created</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {sortedCases.length === 0 ? (
            <p className="text-center text-gray-500 py-4 text-sm">No cases found</p>
          ) : (
            sortedCases.map((caseItem: any) => (
              <div
                key={caseItem.id}
                onClick={() => navigate(`/cases/${caseItem.id}`)}
                className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-sm font-mono">{caseItem.caseNumber}</span>
                      <Badge variant={getStatusColor(caseItem.status)} className="text-xs">
                        {caseItem.status.replace('_', ' ')}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-600 font-medium">{caseItem.caseType}</p>
                    <p className="text-xs text-gray-500 mt-1 line-clamp-1">{caseItem.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      {caseItem.client && (
                        <span>👤 {caseItem.client.firstName} {caseItem.client.lastName}</span>
                      )}
                      <span>
                        Updated: {new Date(caseItem.updatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
}
